import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import Logger from "../../services/Logger";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import StatsCrossService from "../../services/StatsCrossService";
import StatsGridService from "../../services/StatsGridService";

const PUZZLE_TYPE_LABELS = {
    WORD_SEARCH: "Word Search",
    HIDDEN_MESSAGE: "Hidden Message",
    CROSSWORD: "Crossword"
};

export default function PuzzleStats({ puzzleType = "HIDDEN_MESSAGE" }) {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDiscarded, setShowDiscarded] = useState(false);

    const puzzleTypeLabel = puzzleType ? (PUZZLE_TYPE_LABELS[puzzleType] || puzzleType) : "Ready";

    useEffect(() => {
        loadDashboardData();
    }, [puzzleType]);

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);
            // Use StatsCrossService for CROSSWORD, StatsGridService for others
            const statsService = puzzleType === "CROSSWORD" ? StatsCrossService : StatsGridService;
            const data = await statsService.getDashboardStats(puzzleType);
            // Logger.log("=== DASHBOARD DATA STRUCTURE ===");
            // Logger.log("Full dataset:", data);
            // Logger.log("Genres:", data.genres);
            // Logger.log("Overview stats:", data.overview);
            // Logger.log("Difficulty stats:", data.difficultyStats);

            // Log individual genre structure
            // if (data.genres && data.genres.length > 0) {
            //     Logger.log("=== GENRE STRUCTURE EXAMPLE ===");
            //     Logger.log("First genre:", data.genres[0]);
            //     Logger.log("Genre stats structure:", data.genres[0].stats);
            // }

            setDashboardData(data);
        } catch (err) {
            Logger.error("Error loading dashboard data:", err);
            setError("Failed to load dashboard data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const DifficultySplit = ({ easy, medium, hard }) => {
        const total = easy + medium + hard;

        // If all counts are 0, show a dash instead
        if (total === 0) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="h6" sx={{ color: '#666', fontWeight: 'bold' }}>
                        -
                    </Typography>
                </Box>
            );
        }

        const easyPercentage = (easy / total) * 100;
        const mediumPercentage = (medium / total) * 100;
        const hardPercentage = (hard / total) * 100;
        const isEasyTooHigh = easyPercentage > 30;

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Tooltip
                    title={`Easy: ${easy} (${Math.round(easyPercentage)}%) - Goal is ≤30%`}
                    arrow
                    placement="top"
                >
                    <Box sx={{
                        flex: 1,
                        textAlign: 'center',
                        borderRight: '1px solid #e0e0e0',
                        padding: '8px 12px',
                        border: '2px solid #4caf50',
                        cursor: 'help'
                    }}>
                        <Typography variant="h6" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                            {easy}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#4caf50', fontSize: '10px' }}>
                            {Math.round(easyPercentage)}%
                        </Typography>
                    </Box>
                </Tooltip>
                <Tooltip
                    title={`Medium: ${medium} (${Math.round(mediumPercentage)}%)`}
                    arrow
                    placement="top"
                >
                    <Box sx={{
                        flex: 1,
                        textAlign: 'center',
                        borderRight: '1px solid #e0e0e0',
                        padding: '8px 12px',
                        cursor: 'help'
                    }}>
                        <Typography variant="h6" sx={{ color: '#ff9800', fontWeight: 'bold' }}>
                            {medium}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#ff9800', fontSize: '10px' }}>
                            {Math.round(mediumPercentage)}%
                        </Typography>
                    </Box>
                </Tooltip>
                <Tooltip
                    title={`Hard: ${hard} (${Math.round(hardPercentage)}%) - Goal is ≥70%`}
                    arrow
                    placement="top"
                >
                    <Box sx={{
                        flex: 1,
                        textAlign: 'center',
                        padding: '8px 12px',
                        border: isEasyTooHigh ? '2px solid #f44336' : 'none',
                        cursor: 'help'
                    }}>
                        <Typography variant="h6" sx={{ color: '#f44336', fontWeight: 'bold' }}>
                            {hard}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#f44336', fontSize: '10px' }}>
                            {Math.round(hardPercentage)}%
                        </Typography>
                    </Box>
                </Tooltip>
            </Box>
        );
    };

    const formatCount = (count) => {
        return count === 0 ? '-' : count;
    };

    if (loading) {
        return (
            <Box sx={{
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px'
            }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ ml: 2 }}>
                    Loading {puzzleTypeLabel} statistics...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ flexGrow: 1 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            </Box>
        );
    }

    if (!dashboardData) {
        return (
            <Box sx={{ flexGrow: 1 }}>
                <Alert severity="warning">
                    No {puzzleTypeLabel} data available.
                </Alert>
            </Box>
        );
    }

    return (
        <Grid container spacing={2} sx={{ p: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            {/* Genre Distribution Accordion */}
            <Grid size={{ xs: 12 }} sx={{ width: '100%' }}>
                <Accordion sx={{ mt: 2, width: '100%' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ flexDirection: 'row-reverse', '& .MuiAccordionSummary-content': { flex: 1, alignItems: 'center', justifyContent: 'space-between', pr: 1 } }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                Genre Distribution
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Overview of puzzle counts by genre and status
                            </Typography>
                        </Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showDiscarded}
                                    onChange={(e) => setShowDiscarded(e.target.checked)}
                                    onClick={(e) => e.stopPropagation()}
                                    color="primary"
                                />
                            }
                            label="Show discarded puzzles"
                        />
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                <TableContainer component={Paper} sx={{ width: '100%' }}>
                    <Table sx={{ width: '100%', minWidth: 650 }} aria-label="puzzle statistics table">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Genre</TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 'bold',
                                        bgcolor: 'grey.400',
                                        color: 'white'
                                    }}
                                >
                                    Draft
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 'bold',
                                        bgcolor: 'info.light',
                                        color: 'white'
                                    }}
                                >
                                    Ready to Review
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 'bold',
                                        bgcolor: 'warning.light',
                                        color: 'white'
                                    }}
                                >
                                    Reviewed
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 'bold',
                                        bgcolor: 'success.light',
                                        color: 'white'
                                    }}
                                >
                                    Published
                                </TableCell>
                                {showDiscarded && (
                                    <TableCell
                                        align="center"
                                        sx={{
                                            fontWeight: 'bold',
                                            bgcolor: 'error.light',
                                            color: 'white'
                                        }}
                                    >
                                        Discarded
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dashboardData.genres.map((genre) => (
                                <TableRow key={genre.name}>
                                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                        {genre.name}
                                    </TableCell>
                                    <TableCell align="center">
                                        {formatCount(genre.stats.DRAFT)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {formatCount(genre.stats.READY_TO_REVIEW)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {formatCount(genre.stats.REVIEWED)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {formatCount(genre.stats.PUBLISHED)}
                                    </TableCell>
                                    {showDiscarded && (
                                        <TableCell align="center">
                                            {formatCount(genre.stats.DISCARDED)}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                                <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', bgcolor: 'primary.light', color: 'white' }}>
                                    Total
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    {formatCount(dashboardData.genres.reduce((sum, genre) => sum + genre.stats.DRAFT, 0))}
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    {formatCount(dashboardData.genres.reduce((sum, genre) => sum + genre.stats.READY_TO_REVIEW, 0))}
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    {formatCount(dashboardData.genres.reduce((sum, genre) => sum + genre.stats.REVIEWED, 0))}
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    {formatCount(dashboardData.genres.reduce((sum, genre) => sum + genre.stats.PUBLISHED, 0))}
                                </TableCell>
                                {showDiscarded && (
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                        {formatCount(dashboardData.genres.reduce((sum, genre) => sum + genre.stats.DISCARDED, 0))}
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                    </AccordionDetails>
                </Accordion>
            </Grid>

            {/* Difficulty Level Distribution Accordion */}
            <Grid size={{ xs: 12 }} sx={{ width: '100%' }}>
                <Accordion defaultExpanded sx={{ width: '100%' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ flexDirection: 'row-reverse', '& .MuiAccordionSummary-content': { flex: 1, alignItems: 'center', justifyContent: 'space-between', pr: 1 } }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                Difficulty Level Distribution
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Goal: 30% Easy, 70% Hard. Genres with red borders need more Hard puzzles.
                            </Typography>
                        </Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showDiscarded}
                                    onChange={(e) => setShowDiscarded(e.target.checked)}
                                    onClick={(e) => e.stopPropagation()}
                                    color="primary"
                                />
                            }
                            label="Show discarded puzzles"
                        />
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                <TableContainer component={Paper} sx={{ width: '100%' }}>
                    <Table sx={{ width: '100%', minWidth: 650 }} aria-label="difficulty level table">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Genre</TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 'bold',
                                        bgcolor: 'grey.400',
                                        color: 'white'
                                    }}
                                >
                                    Draft
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 'bold',
                                        bgcolor: 'info.light',
                                        color: 'white'
                                    }}
                                >
                                    Ready to Review
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 'bold',
                                        bgcolor: 'warning.light',
                                        color: 'white'
                                    }}
                                >
                                    Reviewed
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 'bold',
                                        bgcolor: 'success.light',
                                        color: 'white'
                                    }}
                                >
                                    Published
                                </TableCell>
                                {showDiscarded && (
                                    <TableCell
                                        align="center"
                                        sx={{
                                            fontWeight: 'bold',
                                            bgcolor: 'error.light',
                                            color: 'white'
                                        }}
                                    >
                                        Discarded
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dashboardData.genres.map((genre) => {
                                // Find the corresponding difficulty stats for this genre
                                const difficultyData = dashboardData.difficultyStats?.find(d => d.name === genre.name);

                                return (
                                    <TableRow key={genre.name}>
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                            {genre.name}
                                        </TableCell>
                                        <TableCell align="center">
                                            <DifficultySplit
                                                easy={difficultyData?.difficultyStats.DRAFT.EASY || 0}
                                                medium={difficultyData?.difficultyStats.DRAFT.MEDIUM || 0}
                                                hard={difficultyData?.difficultyStats.DRAFT.HARD || 0}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <DifficultySplit
                                                easy={difficultyData?.difficultyStats.READY_TO_REVIEW.EASY || 0}
                                                medium={difficultyData?.difficultyStats.READY_TO_REVIEW.MEDIUM || 0}
                                                hard={difficultyData?.difficultyStats.READY_TO_REVIEW.HARD || 0}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <DifficultySplit
                                                easy={difficultyData?.difficultyStats.REVIEWED.EASY || 0}
                                                medium={difficultyData?.difficultyStats.REVIEWED.MEDIUM || 0}
                                                hard={difficultyData?.difficultyStats.REVIEWED.HARD || 0}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <DifficultySplit
                                                easy={difficultyData?.difficultyStats.PUBLISHED.EASY || 0}
                                                medium={difficultyData?.difficultyStats.PUBLISHED.MEDIUM || 0}
                                                hard={difficultyData?.difficultyStats.PUBLISHED.HARD || 0}
                                            />
                                        </TableCell>
                                        {showDiscarded && (
                                            <TableCell align="center">
                                                <DifficultySplit
                                                    easy={difficultyData?.difficultyStats.DISCARDED.EASY || 0}
                                                    medium={difficultyData?.difficultyStats.DISCARDED.MEDIUM || 0}
                                                    hard={difficultyData?.difficultyStats.DISCARDED.HARD || 0}
                                                />
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                                <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', bgcolor: 'primary.light', color: 'white' }}>
                                    Total
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    <DifficultySplit
                                        easy={dashboardData.difficultyStats?.reduce((sum, genre) => sum + (genre.difficultyStats.DRAFT.EASY || 0), 0) || 0}
                                        medium={dashboardData.difficultyStats?.reduce((sum, genre) => sum + (genre.difficultyStats.DRAFT.MEDIUM || 0), 0) || 0}
                                        hard={dashboardData.difficultyStats?.reduce((sum, genre) => sum + (genre.difficultyStats.DRAFT.HARD || 0), 0) || 0}
                                    />
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    <DifficultySplit
                                        easy={dashboardData.difficultyStats?.reduce((sum, genre) => sum + (genre.difficultyStats.READY_TO_REVIEW.EASY || 0), 0) || 0}
                                        medium={dashboardData.difficultyStats?.reduce((sum, genre) => sum + (genre.difficultyStats.READY_TO_REVIEW.MEDIUM || 0), 0) || 0}
                                        hard={dashboardData.difficultyStats?.reduce((sum, genre) => sum + (genre.difficultyStats.READY_TO_REVIEW.HARD || 0), 0) || 0}
                                    />
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    <DifficultySplit
                                        easy={dashboardData.difficultyStats?.reduce((sum, genre) => sum + (genre.difficultyStats.REVIEWED.EASY || 0), 0) || 0}
                                        medium={dashboardData.difficultyStats?.reduce((sum, genre) => sum + (genre.difficultyStats.REVIEWED.MEDIUM || 0), 0) || 0}
                                        hard={dashboardData.difficultyStats?.reduce((sum, genre) => sum + (genre.difficultyStats.REVIEWED.HARD || 0), 0) || 0}
                                    />
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    <DifficultySplit
                                        easy={dashboardData.difficultyStats?.reduce((sum, genre) => sum + (genre.difficultyStats.PUBLISHED.EASY || 0), 0) || 0}
                                        medium={dashboardData.difficultyStats?.reduce((sum, genre) => sum + (genre.difficultyStats.PUBLISHED.MEDIUM || 0), 0) || 0}
                                        hard={dashboardData.difficultyStats?.reduce((sum, genre) => sum + (genre.difficultyStats.PUBLISHED.HARD || 0), 0) || 0}
                                    />
                                </TableCell>
                                {showDiscarded && (
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                        <DifficultySplit
                                            easy={dashboardData.difficultyStats?.reduce((sum, genre) => sum + (genre.difficultyStats.DISCARDED.EASY || 0), 0) || 0}
                                            medium={dashboardData.difficultyStats?.reduce((sum, genre) => sum + (genre.difficultyStats.DISCARDED.MEDIUM || 0), 0) || 0}
                                            hard={dashboardData.difficultyStats?.reduce((sum, genre) => sum + (genre.difficultyStats.DISCARDED.HARD || 0), 0) || 0}
                                        />
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                    </AccordionDetails>
                </Accordion>
            </Grid>
        </Grid>
    );
}

